/**
 * Auth helpers for recording media URLs.
 *
 * Recording files are served by the graphql REST route, authenticated via
 * `helpers/requireAuth.js` which accepts a JWT from `?token=`, the
 * Authorization header, or the SESSIONID cookie.
 *
 * Native (Capacitor WKWebView): the cookie is cross-origin (WebView origin is
 * `https://localhost`, media is `https://messenger.scrile.com`) and the native
 * media loader doesn't carry an Authorization header — so the ONLY option is
 * embedding the short-lived JWT as a `?token=` query param.
 *
 * Web: historically relied on the SESSIONID cookie (synced by App.tsx). But an
 * `<img>`/`<video>` paints and fires its request BEFORE that cookie is set on a
 * fresh load, so the first thumbnail/poster fetch 401s (no token at all) and
 * only recovers on a later re-sync/reload. To make previews load immediately
 * and reliably for every chat member, web ALSO appends a fresh `?token=` now —
 * the same race-free mechanism native already uses. The short-lived token in
 * the URL is the same exposure native/admin already accept (server logs only).
 */
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { getAuthToken } from "./providers";

const isNative = Capacitor.isNativePlatform();

/** Append `?token=<jwt>` to `url`. No-op when the URL already has one. */
function appendTokenParam(url: string, token: string): string {
  try {
    const u = new URL(url, window.location.origin);
    if (u.searchParams.has("token")) return url;
    u.searchParams.set("token", token);
    return u.toString();
  } catch {
    // Malformed URL — fall back to naive concat so we don't break the
    // element entirely.
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}token=${encodeURIComponent(token)}`;
  }
}

/**
 * Resolve a recording media URL with the current auth token attached on
 * mobile. On web, returns the URL unchanged (cookie auth is already in
 * place). On Capacitor the hook fetches the token asynchronously, so the
 * first render returns the raw URL — callers tolerating a brief 401 retry
 * (e.g. `<video>`) are fine; for one-shot fetches use `authedFileUrl()`
 * below instead.
 *
 * Overloaded so callers passing a guaranteed `string` get a `string`
 * back, while callers passing optional thumbnails (recordings without
 * a generated poster) get `null` propagated through to `poster={...}`
 * which then drops the attribute.
 */
export function useAuthedFileUrl(url: string): string;
export function useAuthedFileUrl(url: string | null | undefined): string | null;
export function useAuthedFileUrl(url: string | null | undefined): string | null {
  // First render returns the raw URL (or null if input was null) so the
  // overloaded `(string) => string` contract holds for synchronous
  // consumers like `playableUrl = useAuthedFileUrl(url)`. On mobile
  // this means the platform's media loader may attempt one unauthed
  // fetch (401) before the useEffect re-attaches the token; the
  // failure is silent and superseded by the authed retry.
  const [authed, setAuthed] = useState<string | null>(url || null);
  useEffect(() => {
    if (!url) {
      setAuthed(null);
      return;
    }
    // Both web and native attach a fresh `?token=`. On the very first render
    // the raw URL is used (one possible 401 for a tokenless <img>), then the
    // token resolves (in-memory, ~immediate) and the authed URL supersedes it.
    let cancelled = false;
    void getAuthToken().then((token) => {
      if (cancelled) return;
      setAuthed(token ? appendTokenParam(url, token) : url);
    });
    return () => { cancelled = true; };
  }, [url]);
  return authed;
}

/** One-shot, awaitable version of {@link useAuthedFileUrl}. */
export async function authedFileUrl(url: string): Promise<string> {
  if (!isNative) return url;
  const token = await getAuthToken();
  return token ? appendTokenParam(url, token) : url;
}
